import React, { ChangeEvent, FormEvent } from "react";
import uniq from "lodash/uniq";
import styled, { createGlobalStyle } from "styled-components";
import {
  Button,
  FormGroup,
  InputGroup,
  MenuItem,
  TextArea,
} from "@blueprintjs/core";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mantine/core";

import { MenuMultiSelect, MenuSelect } from "@components";
import {
  ALL_CATEGORIES,
  ALL_LANGUAGES,
  NO_CATEGORY,
  OWASP_2017,
  OWASP_2021,
  TECHNOLOGIES,
} from "@utils";

import { CWE_NAMES } from "@pages/editor/constants";
import CWE_DESCRIPTIONS from "@pages/editor/constants/cwe-descriptions.json";
import { RegistryRuleMetadata } from "@pages/registry/types";
import {
  owaspKeyToString,
  owaspStringToKey,
} from "@pages/registry/utils/owaspStringToId";

import { AddButton } from "./AddButton";
import { DeleteButton } from "./DeleteButton";

/*
example output:

metadata:
    owasp: 'A1: Injection'
    cwe: "CWE-22: Improper Limitation of a Pathname to a Restricted Directory ('Path Traversal')"
    source-rule-url: https://find-sec-bugs.github.io/bugs.htm#PATH_TRAVERSAL_IN
    references:
    - https://www.owasp.org/index.php/Path_Traversal
    category: security
    technology:
    - jax-rs
*/

// ~/semgrep-rules (develop)> rg --no-line-number --no-filename category: | sort | uniq
/* const rule_categories: string[] = [
   "correctness",
   "security",
 "best-practice",
 "caching",
 "compatibility",
 "correctness",
 "maintainability",
 "performance",
 "portability",
] */

const addOrRemove = <T,>(arr: Array<T>, item: T): Array<T> =>
  arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item];

// Make all inputs full width
const MetadataContainer = styled.div`
  .bp3-popover-target {
    display: block;
  }
  .bp3-input {
    width: 100%;
  }
  .bp3-tag {
    max-width: 280px; // max size of tag, so at least two tags can fit into one line
  }
`;

const MetadataPopoversOverrideStyle = createGlobalStyle`
  html .metadata-popover .bp3-menu {
    max-width: 600px;
  }
`;

export const MetadataRow = styled.div`
  display: flex;
  .bp3-label {
    width: 120px;
    flex-grow: 0;
    flex-shrink: 0;
  }
  .bp3-form-content {
    flex-grow: 1;
  }
  .bp3-form-group {
    width: 100%;
  }
  .wide-meta-data-layout & .category-select {
    .bp3-label {
      width: auto;
    }
  }
`;

const SplitRow = styled.div`
  .wide-meta-data-layout & {
    display: flex;
    gap: 32px;
    & > div {
      flex: 1;
    }
  }
`;

interface IRuleMetadataEditorProps {
  metadata: RegistryRuleMetadata;
  setMetadata: (_: RegistryRuleMetadata) => void;
  message: string;
  setMessage: (s: string) => void;
  ruleId: string;
  setRuleId: (s: string) => void;
  languages: string[];
  setLanguages: (s: string[]) => void;
  showOptional: boolean;
  wideLayout: boolean;
}

const defaultCWEs: number[] = [
  // put common CWEs first in our list
  324, 338, 798, 321, 259, 242, 330, 261, 128, 123, 134, 209, 215, 311, 425,
  444, 522, 523, 548, 613, 620, 640, 644, 776, 784, 90, 91, 79, 287, 77, 352,
  310, 400, 113, 200, 119, 657, 94, 918, 89, 601, 611, 307, 840, 922, 602, 489,
];
const missingCwes = Object.keys(CWE_DESCRIPTIONS)
  .map((n) => parseInt(n, 10))
  .filter((n) => !defaultCWEs.includes(n));
const allCWEs: number[] = defaultCWEs.concat(missingCwes).sort((a, b) => a - b);

const ellipsize = (s: string | undefined, charCount: number): string => {
  /*
  Return a string with at most charCount characters, where the last three chars are `...`
  if any of the original string was trimmed.
  */
  if (s === undefined) return "";
  if (s.length >= charCount - 3) {
    return s.slice(0, charCount - 3) + "...";
  } else {
    return s.slice(0, charCount);
  }
};

function correctNewValue(val: string[]): string | string[] | undefined {
  if (val.length === 0) {
    return undefined;
  }
  if (val.length === 1) {
    return val[0];
  }
  return val;
}
export const RuleMetadataEditor: React.FC<IRuleMetadataEditorProps> = ({
  metadata,
  setMetadata,
  message,
  setMessage,
  ruleId,
  setRuleId,
  languages,
  setLanguages,
  showOptional,
  wideLayout,
}) => {
  const setMetadataSafe = (meta: RegistryRuleMetadata) => {
    /*
    In this component, we use use undefined exclusively to represent "no value."
    But when the `metadata` is converted to YAML, it will add the keys that have
    undefined value  in the output YAML as key: null. So we want to drop undefined
    keys entirely from the object, rather than setting them to `undefined`.
    */
    setMetadata(dropUndefinedKeys(meta));
  };

  const getCWEDescription = (cwe: number): string | undefined => {
    return CWE_DESCRIPTIONS[cwe.toString() as keyof typeof CWE_DESCRIPTIONS];
  };

  const getCWEName = (cwe: number): string | undefined => {
    return CWE_NAMES[cwe] || ellipsize(getCWEDescription(cwe), 200); // this ellipsizes the value that goes to the data, for the UI we use css ellipsis
  };

  const cwePredicate = (q: string, cwe: number | string): boolean => {
    if (typeof cwe === "string") {
      return false;
    }
    const lowerQ = q.toLowerCase();
    return (
      cwe.toString().toLowerCase().includes(lowerQ) ||
      (getCWEName(cwe) || "").toLowerCase().includes(lowerQ) ||
      (getCWEDescription(cwe) || "").toLowerCase().includes(lowerQ)
    );
  };

  const toggleTechnology = (technologies: string[], tech: string) => {
    const technology = addOrRemove(technologies, tech);
    if (technology[0] === undefined) {
      setMetadataSafe({ ...metadata, technology: undefined });
    } else {
      setMetadata({ ...metadata, technology });
    }
  };

  const toggleLanguage = (languages: string[], tech: string) => {
    const newLanguages = addOrRemove(languages, tech);
    setLanguages(newLanguages);
  };

  const setCategory = (category: string) => {
    if (category === NO_CATEGORY) {
      setMetadataSafe({ ...metadata, category: undefined });
    } else {
      setMetadata({ ...metadata, category });
    }
  };

  const setSourceRuleUrl = (url: string) => {
    if (url === "") {
      setMetadataSafe({
        ...metadata,
        source_rule_url: undefined,
      });
    } else {
      setMetadata({
        ...metadata,
        source_rule_url: url,
      });
    }
  };

  const dropUndefinedKeys = (o: Object) => {
    return Object.fromEntries(
      Object.entries(o).filter(([_, v]) => v !== undefined)
    );
  };

  const setReferences = (refs: string[]) => {
    if (refs.length === 0) {
      setMetadataSafe({ ...metadata, references: undefined });
    } else {
      setMetadata({
        ...metadata,
        references: refs,
      });
    }
  };

  const hackyCWEStringToNumber = (cweString: string): number | undefined => {
    // just find the first number on the string and return that
    const numStr = cweString.match(/(\d+)/);
    return numStr && numStr.length >= 1 && numStr[0]
      ? parseInt(numStr[0], 10)
      : undefined;
  };

  // we have to add runtime type checks and be conservative, because the user may not have conformed to
  // our expected schema while editing metadata in the YAML
  const selectedCategoryIsSecurity: boolean = metadata?.category === "security";
  const cwes =
    typeof metadata.cwe === "string"
      ? [metadata.cwe]
      : Array.isArray(metadata.cwe)
      ? metadata.cwe
      : [];

  const cwesNums = cwes.map(
    (cwe) => hackyCWEStringToNumber(cwe) ?? String(cwe)
  );

  const owasps =
    typeof metadata.owasp === "string"
      ? [metadata.owasp]
      : Array.isArray(metadata.owasp)
      ? metadata.owasp
      : [];

  const owaspIds = owasps.map(
    (owasp) => owaspStringToKey(owasp) ?? String(owasp)
  );

  const references: string[] =
    metadata.references !== undefined && Array.isArray(metadata.references)
      ? metadata.references
      : [];
  const technologies =
    metadata.technology && Array.isArray(metadata.technology)
      ? metadata.technology
      : [];
  const category =
    metadata.category && typeof metadata.category === "string"
      ? metadata.category
      : undefined;

  function cweToString(i: number | string) {
    if (typeof i === "number") {
      return `CWE ${i}: ${getCWEName(i) || "unknown"}`;
    } else {
      return i;
    }
  }

  return (
    <MetadataContainer className={wideLayout ? "wide-meta-data-layout" : ""}>
      <MetadataPopoversOverrideStyle />
      <MetadataRow>
        <FormGroup
          inline
          label="Rule ID"
          labelFor="rule-id"
          intent={ruleId.length === 0 ? "danger" : "none"}
          helperText={ruleId.length === 0 ? "Required" : ""}
        >
          <InputGroup
            id="rule-id"
            intent={ruleId.length === 0 ? "danger" : "none"}
            onChange={(e: FormEvent<HTMLInputElement>) =>
              setRuleId(e.currentTarget.value)
            }
            value={ruleId}
          />
        </FormGroup>
      </MetadataRow>

      <SplitRow>
        <MetadataRow>
          <FormGroup
            inline
            label="Languages"
            labelFor="languages"
            intent={languages.length === 0 ? "danger" : "none"}
            helperText={languages.length === 0 ? "Required" : ""}
          >
            <MenuMultiSelect<string>
              createNewItemFromQuery={(q) => q}
              createNewItemRenderer={(
                query: string,
                active: boolean,
                handleClick: React.MouseEventHandler<HTMLElement>
              ) => (
                <MenuItem
                  icon="add"
                  text={`Add new: ${query}`}
                  active={active}
                  onClick={handleClick}
                  shouldDismissPopover={true}
                />
              )}
              items={Object.values(ALL_LANGUAGES).map((lang) => lang.id)}
              selectedItems={languages}
              onRemove={(t: string) => toggleLanguage(languages, t)}
              onItemSelect={(t: string) => toggleLanguage(languages, t)}
              extractor={(i) => i}
              isItemSelected={(i: string) => languages.indexOf(i) >= 0}
              tagInputProps={{
                intent: languages.length === 0 ? "danger" : "none",
              }}
              popoverProps={{
                className: "metadata-popover",
              }}
            />
          </FormGroup>
        </MetadataRow>
        <MetadataRow
          style={{
            flexGrow: "0",
            flexBasis: "300px",
          }}
        >
          <FormGroup
            inline
            className="category-select"
            label="Category"
            labelFor="category"
            intent={!category ? "danger" : "none"}
            helperText={!category ? "Required" : ""}
          >
            <MenuSelect
              items={ALL_CATEGORIES}
              onItemSelect={setCategory}
              extractor={(i) => i}
              isItemSelected={(i: string) => category === i}
            >
              <Button
                id="category"
                fill
                rightIcon="caret-down"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                {category || "Select category"}
              </Button>
            </MenuSelect>
          </FormGroup>
        </MetadataRow>
      </SplitRow>

      <MetadataRow>
        <FormGroup
          inline
          label={
            <span
              style={{
                whiteSpace: "nowrap",
              }}
            >
              Message{" "}
              <span style={{ display: "inline-block" }}>
                <Tooltip
                  withArrow
                  withinPortal
                  label={
                    <>You can use metavariables (like $FOO) in the message</>
                  }
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    size={"xs"}
                    color={"var(--global-mid-gray)"}
                  />
                </Tooltip>
              </span>
            </span>
          }
          labelFor="rule-message"
          intent={message.length === 0 ? "danger" : "none"}
          helperText={message.length === 0 ? "Required" : ""}
        >
          <TextArea
            id="rule-message"
            onChange={(e: FormEvent<HTMLTextAreaElement>) =>
              setMessage(e.currentTarget.value)
            }
            fill
            value={message}
            intent={message.length === 0 ? "danger" : "none"}
          />
        </FormGroup>
      </MetadataRow>

      {selectedCategoryIsSecurity && (
        <>
          <MetadataRow>
            <FormGroup
              inline
              label="CWE"
              labelFor="cwe"
              intent={cwesNums.length === 0 ? "danger" : "none"}
              helperText={cwesNums.length === 0 ? "Required" : ""}
            >
              <MenuMultiSelect<number | string>
                className="cwe-select"
                tagInputProps={{
                  intent: cwesNums.length === 0 ? "danger" : "none",
                }}
                createNewItemFromQuery={(q) => q}
                createNewItemRenderer={(
                  query: string,
                  active: boolean,
                  handleClick: React.MouseEventHandler<HTMLElement>
                ) => (
                  <MenuItem
                    icon="add"
                    text={`Add new: ${query}`}
                    active={active}
                    onClick={handleClick}
                    shouldDismissPopover={true}
                  />
                )}
                items={allCWEs}
                selectedItems={cwesNums}
                onRemove={(t: number | string) => {
                  const newCwes = cwes.filter((cwe) => {
                    const cweNum = hackyCWEStringToNumber(cwe);
                    return cweNum !== t && cwe !== String(t);
                  });
                  setMetadataSafe({
                    ...metadata,
                    cwe: correctNewValue(newCwes),
                  });
                }}
                onItemSelect={(i: number | string) => {
                  const newCwes = uniq([...cwes, cweToString(i)]);
                  setMetadata({
                    ...metadata,
                    cwe: correctNewValue(newCwes),
                  });
                }}
                extractor={cweToString}
                isItemSelected={(i: number | string) => {
                  if (typeof i === "number") {
                    return cwesNums.indexOf(i) >= 0;
                  } else {
                    return false;
                  }
                }}
                popoverProps={{
                  portalClassName: "metadata-popover",
                }}
                itemPredicate={cwePredicate}
              />
            </FormGroup>
          </MetadataRow>
          <MetadataRow>
            <FormGroup
              inline
              label="OWASP"
              labelFor="owasp"
              intent={owaspIds.length === 0 ? "danger" : "none"}
              helperText={owaspIds.length === 0 ? "Required" : ""}
            >
              <MenuMultiSelect<string>
                className="owasp-select"
                tagInputProps={{
                  intent: owaspIds.length === 0 ? "danger" : "none",
                }}
                createNewItemFromQuery={(q) => q}
                createNewItemRenderer={(
                  query: string,
                  active: boolean,
                  handleClick: React.MouseEventHandler<HTMLElement>
                ) => (
                  <MenuItem
                    icon="add"
                    text={`Add new: ${query}`}
                    active={active}
                    onClick={handleClick}
                    shouldDismissPopover={true}
                  />
                )}
                items={[
                  ...Object.keys(OWASP_2021).map(
                    (key) => `${Number(key) + 1}:2021`
                  ),
                  ...Object.keys(OWASP_2017).map(
                    (key) => `${Number(key) + 1}:2017`
                  ),
                ]}
                selectedItems={owaspIds}
                onRemove={(t) => {
                  const newOwasps = owasps.filter((owasp) => {
                    return owaspStringToKey(owasp) !== t && owasp !== t;
                  });
                  setMetadataSafe({
                    ...metadata,
                    owasp: correctNewValue(newOwasps),
                  });
                }}
                onItemSelect={(t) => {
                  const newOwasp = owaspKeyToString(t) ?? t;
                  const newOwasps = uniq([...owasps, newOwasp]);
                  setMetadata({
                    ...metadata,
                    owasp: correctNewValue(newOwasps),
                  });
                }}
                extractor={(t) => owaspKeyToString(t) ?? t}
                isItemSelected={(t) => owaspIds.indexOf(t) >= 0}
                popoverProps={{
                  className: "metadata-popover",
                }}
              />
            </FormGroup>
          </MetadataRow>
        </>
      )}

      {showOptional && (
        <MetadataRow>
          <FormGroup inline label="Technologies" labelFor="technologies">
            <MenuMultiSelect<string>
              createNewItemFromQuery={(q) => q}
              createNewItemRenderer={(
                query: string,
                active: boolean,
                handleClick: React.MouseEventHandler<HTMLElement>
              ) => (
                <MenuItem
                  icon="add"
                  text={`Add new: ${query}`}
                  active={active}
                  onClick={handleClick}
                  shouldDismissPopover={true}
                />
              )}
              items={TECHNOLOGIES}
              selectedItems={technologies}
              onRemove={(t: string) => toggleTechnology(technologies, t)}
              onItemSelect={(t: string) => toggleTechnology(technologies, t)}
              extractor={(i) => i}
              isItemSelected={(i: string) => technologies.indexOf(i) >= 0}
              popoverProps={{
                className: "metadata-popover",
              }}
            />
          </FormGroup>
        </MetadataRow>
      )}

      {showOptional && (
        <MetadataRow>
          <FormGroup inline label="Rule source URL" labelFor="rule-source-url">
            <InputGroup
              id="rule-source-url"
              placeholder="https://example.com"
              value={metadata.source_rule_url ?? ""}
              onChange={(e) => setSourceRuleUrl(e.currentTarget.value)}
            />
          </FormGroup>
        </MetadataRow>
      )}
      {showOptional && (
        <MetadataRow>
          <FormGroup inline label="Reference URLs">
            {references.map((ref, i) => {
              return (
                <div key={i} style={{ display: "flex" }}>
                  <div style={{ flex: 1 }}>
                    <InputGroup
                      key={i}
                      placeholder="https://example.com"
                      value={ref}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setReferences([
                          ...references.slice(0, i),
                          e.currentTarget.value,
                          ...references.slice(i + 1),
                        ])
                      }
                    />
                  </div>
                  <div style={{ flexBasis: "30px" }}>
                    <DeleteButton
                      ishidden={references.length === 0}
                      minimal={true}
                      small={true}
                      icon={"cross"}
                      intent={"danger"}
                      onClick={() =>
                        setReferences([
                          ...references.slice(0, i),
                          ...references.slice(i + 1),
                        ])
                      }
                    />
                  </div>
                </div>
              );
            })}

            <AddButton
              minimal={true}
              small={true}
              icon={"plus"}
              intent={"primary"}
              onClick={() => {
                setReferences([...references, "https://example.com"]);
              }}
            />
          </FormGroup>
        </MetadataRow>
      )}
    </MetadataContainer>
  );
};
